import { useState, useEffect, useCallback } from 'react';
import type { 
  Organization, 
  OrganizationFilters,
  PaginatedOrganizationResponse 
} from '../types/organization';
import { organizationService } from '../services/organizationService';

interface UseOrganizationsOptions {
  page?: number;
  limit?: number;
  autoFetch?: boolean;
}

export function useOrganizations(options: UseOrganizationsOptions = {}) {
  const { page = 1, limit = 25, autoFetch = true } = options;
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(page);
  const [pageSize, setPageSize] = useState(limit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrganizationFilters>({});

  const fetchOrganizations = useCallback(async (
    pageNumber?: number,
    searchFilters?: OrganizationFilters,
    newPageSize?: number
  ) => {
    setLoading(true);
    setError(null);
    
    const actualPage = pageNumber ?? currentPage;
    const actualFilters = searchFilters ?? filters;
    const actualPageSize = newPageSize ?? pageSize;
    
    try {
      const response: PaginatedOrganizationResponse = await organizationService.getOrganizations({
        page: actualPage,
        limit: actualPageSize,
        search: actualFilters.search,
        type: actualFilters.type,
        isActive: actualFilters.isActive,
      });
      
      setOrganizations(response.organizations);
      setTotal(response.total);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
      
      // Update page size if provided
      if (newPageSize && newPageSize !== pageSize) {
        setPageSize(newPageSize);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch organizations';
      setError(errorMessage);
      console.error('Error fetching organizations:', err);
    } finally {
      setLoading(false);
    }
  }, [pageSize]); // Remove currentPage and filters from dependencies to prevent infinite loops

  const updateFilters = useCallback((newFilters: OrganizationFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const goToPage = useCallback((pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      fetchOrganizations(pageNumber, filters);
    }
  }, [totalPages, fetchOrganizations, filters]);

  const refresh = useCallback(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const search = useCallback((searchTerm: string) => {
    const newFilters = { ...filters, search: searchTerm };
    updateFilters(newFilters);
    fetchOrganizations(1, newFilters);
  }, [filters, updateFilters, fetchOrganizations]);



  // Auto-fetch on mount only
  useEffect(() => {
    if (autoFetch) {
      fetchOrganizations();
    }
  }, [autoFetch]); // Only depend on autoFetch to prevent infinite loops

  return {
    organizations,
    total,
    totalPages,
    currentPage,
    pageSize,
    loading,
    error,
    filters,
    updateFilters,
    goToPage,
    refresh,
    search,
    fetchOrganizations,
  };
} 